import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import type { KeyStatSurveyItem } from '../../../../models/Report'
import { useReportsContext } from '../../ReportsContext'
import { black } from '../../../../colors'
import { calculateChartHeight } from './BarChart/helper'
import { getAvailableSeriesIndexes, getChartLabelWidth, getScrollableChartWidth, getStackedResponseColors, getTruncatedLabelMetadata } from '../helper'
import { getChartDisplayOptions } from '../questionSampleSize'
import { getReportTitleDisplayItems } from '../reportTitles'

interface StackedChartForMultiChoiceAndMultiSelectProps {
  titles?: string[]
  report: KeyStatSurveyItem
  isHorizontalLayout?: boolean
}

const StackedChartForMultiChoiceAndMultiSelect: React.FC<StackedChartForMultiChoiceAndMultiSelectProps> = ({
  titles,
  report,
  isHorizontalLayout = false
}) => {
  const theme = useTheme()
  const displayOptions = getChartDisplayOptions(report)
  const numTerms = report.options?.[0]?.percents?.length ?? 0
  const displayTitles = getReportTitleDisplayItems(titles ?? []).map(({ displayTitle }) => displayTitle)
  const availableSeriesIndexes = getAvailableSeriesIndexes(report, titles?.length ?? numTerms)
  const xLabels = titles && titles.length === numTerms
    ? availableSeriesIndexes.map((index) => displayTitles[index] ?? titles[index] ?? `Series ${index + 1}`)
    : Array.from({ length: numTerms }, (_, i) => `Term ${i + 1}`)

  const { isFullScreen } = useReportsContext()
  const horizontalLabelWidth = getChartLabelWidth(xLabels, true)
  const chartWidth = getScrollableChartWidth(xLabels, isHorizontalLayout)
  const chartHeight = calculateChartHeight(xLabels.length, isHorizontalLayout, false)
  const responseColors = getStackedResponseColors(displayOptions.map((option: any) => option.name))
  const labelCharacterLimit = isFullScreen ? 26 : 20

  const chartData = displayOptions.map((opt: any, idx: number) => ({
    name: opt.name,
    type: 'bar',
    stack: 'total',
    emphasis: { focus: 'series', label: { show: false } },
    label: {
      show: false,
      fontSize: 14
    },
    itemStyle: {
      color: responseColors[idx % responseColors.length]
    },
    barMaxWidth: isHorizontalLayout ? 45 : 80,
    data: availableSeriesIndexes.map((seriesIndex, dataIndex: number) => {
      const value = opt.percents?.[seriesIndex]
      const numericValue = typeof value === 'number' ? value : 0
      const isVisibleTopSegment = displayOptions
        .slice(idx + 1)
        .every((nextOption: any) => {
          const nextValue = nextOption?.percents?.[seriesIndex]
          return typeof nextValue !== 'number' || nextValue <= 0
        })

      return {
        value: numericValue,
        itemStyle: {
          borderRadius: isVisibleTopSegment
            ? (isHorizontalLayout ? [0, 10, 10, 0] : [10, 10, 0, 0])
            : [0, 0, 0, 0]
        }
      }
    })
  }))

  const option = {
    color: responseColors,
    grid: isHorizontalLayout
      ? { left: horizontalLabelWidth + 24, right: 48, bottom: 44, top: 36 }
      : { left: 60, right: 20, bottom: 88, top: 30 },
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        if (Array.isArray(params) && params.length > 0) {
          const axisLabel = params[0].axisValueLabel || params[0].name || ''
          const lines = [axisLabel]
          params.forEach((item: any) => {
            if (item.value > 0) {
              const val = Number(item.value)
              const valStr = val % 1 === 0 ? val.toFixed(0) : val.toFixed(2)
              lines.push(`${item.marker}${item.seriesName}: ${valStr}%`)
            }
          })
          return lines.join('<br/>')
        }
        return ''
      }
    },
    xAxis: isHorizontalLayout
      ? {
          type: 'value',
          max: 100,
          axisLine: { show: false },
          axisLabel: {
            color: theme.palette.text.secondary,
            fontSize: 14,
            margin: 14,
            formatter: '{value}%'
          },
          axisTick: { show: false },
          splitLine: { show: true }
        }
      : {
          type: 'category',
          data: xLabels,
          axisLine: { show: false },
          axisLabel: {
            color: theme.palette.text.secondary,
            fontSize: 14,
            fontWeight: 500,
            align: 'center',
            interval: 0,
            margin: 18,
            overflow: 'truncate',
            width: 110,
            showMaxLabel: true,
            showMinLabel: true
          },
          axisTick: { show: false },
          splitLine: { show: false }
        },
    yAxis: isHorizontalLayout
      ? {
          type: 'category',
          data: xLabels,
          axisLine: { show: false },
          axisLabel: {
            show: true,
            color: theme.palette.text.secondary,
            fontSize: 14,
            fontWeight: 500,
            margin: 12,
            overflow: 'truncate',
            width: horizontalLabelWidth
          },
          axisTick: { show: false },
          splitLine: { show: false }
        }
      : {
          type: 'value',
          max: 100,
          show: true,
          splitLine: { show: true },
          axisLabel: {
            show: true,
            color: theme.palette.text.secondary,
            fontSize: 14,
            margin: 20,
            formatter: '{value}%'
          },
          axisLine: { show: false },
          axisTick: { show: false }
        },
    series: chartData.map(s => ({ ...s, emphasis: { focus: 'none' } }))
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* Let dense comparison views scroll horizontally instead of hiding category labels. */}
      <Box sx={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', pb: 1 }}>
        <Box sx={{ minWidth: chartWidth }}>
          <ReactECharts
            lazyUpdate
            style={{ width: '100%', height: `${chartHeight}px` }}
            option={option}
            opts={{ renderer: 'svg' }}
          />
        </Box>
      </Box>
      <Box
        data-testid="stacked-chart-legend"
        display="flex"
        flexWrap="wrap"
        justifyContent="center"
        rowGap={2}
        columnGap={4}
        sx={{ pt: 1 }}
      >
        {displayOptions.map((option: any, index: number) => {
          const { displayLabel, tooltipLabel } = getTruncatedLabelMetadata(option.name, labelCharacterLimit)

          return (
            <Box key={option.name} display="flex" alignItems="center" gap={1} minWidth={0}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '999px',
                  backgroundColor: responseColors[index % responseColors.length],
                  flexShrink: 0
                }}
              />
              <Typography
                variant="body2"
                color={black[100]}
                fontWeight={500}
                noWrap
                title={tooltipLabel ?? option.name}
                sx={{ maxWidth: 180 }}
              >
                {displayLabel}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default StackedChartForMultiChoiceAndMultiSelect
